import { apiFetch } from '@/lib/api-client'

const FEEDBACK_REPORTS_BASE = '/feedback/reports'

export type FeedbackStatus = 'received' | 'in_review' | 'resolved'

export type DeliveryChannel = 'email' | 'slack' | 'github'
export type DeliveryChannelState = 'delivered' | 'failed' | 'unknown' | 'skipped'

export interface DeliveryChannelResult {
  channel: DeliveryChannel
  state: DeliveryChannelState
}

export interface FeedbackDeliveryResult {
  attempted: DeliveryChannel[]
  succeeded: DeliveryChannel[]
  failed: DeliveryChannel[]
  unknown: DeliveryChannel[]
  skipped: DeliveryChannel[]
  channels: DeliveryChannelResult[]
}

export interface FeedbackReceipt {
  trackingId: string
  estimatedResponse: string
  deliveryResult: FeedbackDeliveryResult
  deduplicated: boolean
  idempotentReplay: boolean
}

export type FeedbackSubmitErrorKind = 'auth' | 'rate-limit' | 'rejected' | 'ambiguous'

export class FeedbackSubmitError extends Error {
  constructor(
    public readonly kind: FeedbackSubmitErrorKind,
    message: string,
    public readonly retryAfterSeconds?: number,
  ) {
    super(message)
    this.name = 'FeedbackSubmitError'
  }
}

export interface SubmitFeedbackInput {
  url: string
  payload: Record<string, unknown>
  idempotencyKey: string
  token?: string | null
  credentials: RequestCredentials
  includeAuthorization: boolean
}

const CHANNELS = new Set<DeliveryChannel>(['email', 'slack', 'github'])
const STATES = new Set<DeliveryChannelState>(['delivered', 'failed', 'unknown', 'skipped'])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isChannel(value: unknown): value is DeliveryChannel {
  return typeof value === 'string' && CHANNELS.has(value as DeliveryChannel)
}

function isState(value: unknown): value is DeliveryChannelState {
  return typeof value === 'string' && STATES.has(value as DeliveryChannelState)
}

function channelList(value: unknown): DeliveryChannel[] {
  return Array.isArray(value) ? value.filter(isChannel) : []
}

function parseDeliveryResult(data: Record<string, unknown>): FeedbackDeliveryResult {
  const channels = Array.isArray(data.channels)
    ? data.channels.flatMap((item): DeliveryChannelResult[] => {
      if (!isRecord(item) || !isChannel(item.channel) || !isState(item.state)) return []
      return [{ channel: item.channel, state: item.state }]
    })
    : []
  const skipped = channelList(data.skipped)
  const attempted = channelList(data.attempted).filter((channel) => !skipped.includes(channel))
  return {
    attempted,
    succeeded: channelList(data.succeeded).filter((channel) => attempted.includes(channel)),
    failed: channelList(data.failed).filter((channel) => attempted.includes(channel)),
    unknown: channelList(data.unknown).filter((channel) => attempted.includes(channel)),
    skipped,
    channels,
  }
}

function parseRetryAfter(value: string | null): number | undefined {
  if (!value) return undefined
  const seconds = Number(value)
  if (Number.isFinite(seconds) && seconds > 0) return Math.ceil(seconds)
  const timestamp = Date.parse(value)
  if (!Number.isFinite(timestamp)) return undefined
  const remaining = Math.ceil((timestamp - Date.now()) / 1000)
  return remaining > 0 ? remaining : undefined
}

function rejectionMessage(payload: unknown): string | undefined {
  if (!isRecord(payload) || !isRecord(payload.error)) return undefined
  const details = payload.error.details
  if (isRecord(details)) {
    const validation = details.validation_errors
    if (isRecord(validation)) {
      for (const messages of Object.values(validation)) {
        if (Array.isArray(messages) && typeof messages[0] === 'string') return messages[0]
      }
    }
    if (typeof details.detail === 'string') return details.detail
  }
  return typeof payload.error.message === 'string' ? payload.error.message : undefined
}

export async function submitFeedback(input: SubmitFeedbackInput): Promise<FeedbackReceipt> {
  const headers = new Headers({ Accept: 'application/json', 'Content-Type': 'application/json' })
  if (input.includeAuthorization && input.token) headers.set('Authorization', `Bearer ${input.token}`)
  headers.set('Idempotency-Key', input.idempotencyKey)

  let response: Response
  try {
    response = await fetch(input.url, {
      method: 'POST',
      body: JSON.stringify(input.payload),
      credentials: input.credentials,
      headers,
    })
  } catch {
    throw new FeedbackSubmitError('ambiguous', 'Feedback transport result is unknown')
  }

  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    throw new FeedbackSubmitError('ambiguous', 'Feedback response could not be verified')
  }

  if (response.status === 401 || response.status === 403) {
    throw new FeedbackSubmitError('auth', 'Feedback authentication failed')
  }
  if (response.status === 429) {
    throw new FeedbackSubmitError(
      'rate-limit',
      'Feedback rate limit exceeded',
      parseRetryAfter(response.headers.get('Retry-After')),
    )
  }
  if (!response.ok || !isRecord(payload) || payload.success !== true) {
    throw new FeedbackSubmitError('rejected', rejectionMessage(payload) ?? 'Feedback rejected')
  }
  if (!isRecord(payload.data)) {
    throw new FeedbackSubmitError('ambiguous', 'Feedback receipt is missing')
  }
  const trackingId = payload.data.trackingId
  const estimatedResponse = payload.data.estimatedResponse ?? payload.data.eta
  if (typeof trackingId !== 'string' || !trackingId.trim() || typeof estimatedResponse !== 'string' || !estimatedResponse.trim()) {
    throw new FeedbackSubmitError('ambiguous', 'Feedback receipt could not be verified')
  }
  return {
    trackingId,
    estimatedResponse,
    deliveryResult: parseDeliveryResult(payload.data),
    deduplicated: payload.data.deduplicated === true,
    idempotentReplay: payload.data.idempotentReplay === true,
  }
}

export interface FeedbackHistoryItem {
  trackingId: string
  status: FeedbackStatus
  statusLabel: 'Alındı' | 'İnceleniyor' | 'Çözüldü'
  etaText: string
  createdAt: string
  updatedAt: string
  /** Sunucu belirler (yönetici kapısı dahil); yoksa sohbet arayüzü gösterilmez. */
  chatAvailable?: boolean
  chatUnread?: number
  /** Bildirimin geldiği yer; sunucu göndermediyse alan yoktur (site gibi davranılır). */
  source?: 'site' | 'extension'
}

export async function getFeedbackHistory(): Promise<FeedbackHistoryItem[]> {
  const response = await apiFetch<{
    success: boolean
    data: FeedbackHistoryItem[]
  }>(FEEDBACK_REPORTS_BASE)
  return response.data
}

export async function correlateFeedbackSentryEvent(
  trackingId: string,
  eventId: string,
): Promise<{ correlated: boolean }> {
  const response = await apiFetch<{
    success: boolean
    data: { correlated: boolean }
  }>(`${FEEDBACK_REPORTS_BASE}/${trackingId}/sentry-correlation`, {
    method: 'POST',
    body: JSON.stringify({ eventId }),
  })
  return response.data
}
